/**
 * TutorialCaptureEngine - Records user interactions as v2.0 action-diff steps
 *
 * Produces a JSON structure with codeSteps containing PLACE_BLOCK actions
 * and subSteps for OPEN_MENU, TOGGLE_HARDWARE, SET_VALUE, SET_STATE, CLOSE_MENU.
 *
 * Designed to work with TutorialReplayEngine (flattenToMicroSlides / buildStateForMicroSlide).
 */

// ──────────────────────────────────────────────
// Internal type → user-friendly label mapping
// ──────────────────────────────────────────────

const PORT_NAME_MAP = {
    servo_motor1: 'S1',
    servo_motor2: 'S2',
    M3: 'M2',
}

const INTERNAL_TO_JSON_TYPE = {
    hardware: 'action',
    wait: 'wait',
    condition: 'condition',
    loop: 'loop',
    end: 'end',
    repeat: 'end',
    sensor: 'sensor',
    variable: 'variable',
    if: 'if',

    end_loop: 'end_loop',
    end_condition: 'end_condition',
    end_sensor: 'end_sensor',
    end_variable: 'end_variable',
    end_if: 'end_if',
}

const BLOCK_LABELS = {
    action: 'Action',
    wait: 'Wait',
    end: 'Stop',
    condition: 'Decision',
    loop: 'Loop',
    sensor: 'Sensor',
    variable: 'Variable',
    if: 'If',

    end_loop: 'End Repeat',
    end_condition: 'End Decision',
    end_sensor: 'End Decision',
    end_variable: 'End Variable',
    end_if: 'End If',
}

// ──────────────────────────────────────────────
// Grid position computation (mirrors ProgramToDrawing)
// ──────────────────────────────────────────────

/**
 * Walk the program tree identically to ProgramToDrawing's recurse function
 * and return a flat list of { type, row, col, ref } for every node.
 */
export const computeAllBlockPositions = (program) => {
    const positions = []
    let curRow = 1
    let curCol = 1
    let maxCols = 0

    const setCurCol = (val) => {
        curCol = val
        if (curCol > maxCols) maxCols = curCol
    }

    const recurse = (instructions) => {
        if (!Array.isArray(instructions)) return

        for (let i = 0; i < instructions.length; i++) {
            const instruction = instructions[i]

            // 1. If it's a container, process the "inside" (subprogram) first
            if (
                ['variable', 'sensor', 'condition', 'loop'].includes(
                    instruction.type
                )
            ) {
                curRow += 2

                // Save the starting column for the sub-blocks
                const savedCol = curCol

                recurse(instruction.subprogram || [])

                curRow -= 2
                // Reset column to where the container block started
                // so the container block itself is placed correctly
                curCol = savedCol
            }

            // 2. Place the block at the current position
            positions.push({
                type: instruction.type,
                row: curRow,
                col: curCol,
                ref: instruction,
            })

            // 3. Move to the next column for the next block in this array
            setCurCol(curCol + 1)
        }
    }

    recurse(program)
    return positions
}

/**
 * Find the grid position of the last block at a given nesting level.
 */
export const getGridPositionForLastBlock = (program, currentProgramGuide) => {
    const positions = computeAllBlockPositions(program)
    if (positions.length === 0) return { row: 1, col: 1 }

    // Navigate to the correct nesting level to find the last block
    let instructions = program
    for (let level = 0; level < currentProgramGuide; level++) {
        const lastBlock = instructions[instructions.length - 1]
        if (lastBlock && lastBlock.subprogram) {
            instructions = lastBlock.subprogram
        } else break
    }

    // The last block at this level
    const lastBlock = instructions[instructions.length - 1]
    if (!lastBlock) return { row: 1, col: 1 }

    // Find it in positions by reference
    const match = positions.find((p) => p.ref === lastBlock)
    if (match) return { row: match.row, col: match.col }

    // Fallback: approximate
    return { row: 1 + 2 * currentProgramGuide, col: instructions.length }
}

// ──────────────────────────────────────────────
// codeStep / subStep creation helpers
// ──────────────────────────────────────────────

export const generateBoardTarget = (row, col) => `hex-block-${row}-${col}`

export const generateUiTarget = (blockType) => `toolbox_${blockType}`

/**
 * Create a new codeStep for a PLACE_BLOCK action.
 */
export const createCodeStep = (stepId, internalType, gridPosition, message) => {
    const jsonBlockType = INTERNAL_TO_JSON_TYPE[internalType] || internalType
    const label = BLOCK_LABELS[jsonBlockType] || jsonBlockType

    // No row math needed! We will pass the exact clicked coordinates directly.
    return {
        stepId,
        type: 'BLOCK_INTERACTION',
        action: 'PLACE_BLOCK',
        blockType: jsonBlockType,
        gridPosition: { row: gridPosition.row, col: gridPosition.col },
        uiTarget: generateUiTarget(jsonBlockType),
        boardTarget: generateBoardTarget(gridPosition.row, gridPosition.col),
        preMessage:
            stepId === 1
                ? 'First, click the Select block.'
                : 'Click the Select block again.',
        selectMessage: `Choose the ${label} block.`,
        message: message || `The ${label} block has been placed.`,
        subSteps: [],
    }
}

/**
 * Create a new codeStep for an INSERT_BLOCK action (inserting between existing blocks).
 * @param {number} stepId
 * @param {string} internalType - the block type being inserted (e.g. 'hardware', 'loop')
 * @param {Object} gridPosition - { row, col } of the inserted block
 * @param {number} insertIndex - index in the parent array where the block is inserted
 * @param {number[]} nestingPath - array of indices to reach the parent array (empty for top-level)
 * @param {string} [message]
 */
export const createInsertStep = (
    stepId,
    internalType,
    gridPosition,
    insertIndex,
    nestingPath,
    message
) => {
    const jsonBlockType = INTERNAL_TO_JSON_TYPE[internalType] || internalType
    const label = BLOCK_LABELS[jsonBlockType] || jsonBlockType

    return {
        stepId,
        type: 'BLOCK_INTERACTION',
        action: 'INSERT_BLOCK',
        blockType: jsonBlockType,
        gridPosition: { row: gridPosition.row, col: gridPosition.col },
        insertIndex,
        nestingPath: nestingPath || [],
        uiTarget: generateUiTarget(jsonBlockType),
        boardTarget: generateBoardTarget(gridPosition.row, gridPosition.col),
        preMessage: 'Click the block where you want to insert before.',
        selectMessage: `Click the Insert option.`,
        message: message || `The ${label} block has been inserted.`,
        subSteps: [],
    }
}

/**
 * Create a new codeStep for a DELETE_BLOCK action.
 * @param {number} stepId
 * @param {string} internalType - the block type being deleted
 * @param {Object} gridPosition - { row, col } of the deleted block
 * @param {number} deleteIndex - index in the parent array of the block being deleted
 * @param {number[]} nestingPath - array of indices to reach the parent array (empty for top-level)
 * @param {string} [message]
 */
export const createDeleteStep = (
    stepId,
    internalType,
    gridPosition,
    deleteIndex,
    nestingPath,
    message
) => {
    const jsonBlockType = INTERNAL_TO_JSON_TYPE[internalType] || internalType
    const label = BLOCK_LABELS[jsonBlockType] || jsonBlockType

    return {
        stepId,
        type: 'BLOCK_INTERACTION',
        action: 'DELETE_BLOCK',
        blockType: jsonBlockType,
        gridPosition: { row: gridPosition.row, col: gridPosition.col },
        deleteIndex,
        nestingPath: nestingPath || [],
        uiTarget: generateBoardTarget(gridPosition.row, gridPosition.col),
        boardTarget: generateBoardTarget(gridPosition.row, gridPosition.col),
        preMessage: `Click the block which you want to delete.`,
        selectMessage: `Click the Delete option.`,
        message: message || `The ${label} block has been deleted.`,
        subSteps: [],
    }
}

/**
 * Create a subStep object.
 */
export const createSubStep = (parentStepId, subIndex, action, options = {}) => {
    return {
        subStepId: `${parentStepId}-${subIndex}`,
        action,
        uiTarget: options.uiTarget || '',
        ...(options.hardwareKey !== undefined && {
            hardwareKey: options.hardwareKey,
        }),
        ...(options.expectedValue !== undefined && {
            expectedValue: options.expectedValue,
        }),
        message: options.message || '',
    }
}

// ──────────────────────────────────────────────
// State diffing (for panel value changes)
// ──────────────────────────────────────────────

/**
 * Diff old and new panel states to determine what changed.
 * Returns an array of { action, hardwareKey, expectedValue, uiTarget, message }.
 *
 * - Boolean toggling (assign*) → TOGGLE_HARDWARE
 * - Numeric / string value changes (value*) → SET_VALUE
 * - end / repeat state → SET_STATE
 */
export const diffPanelState = (oldState, newState, blockType) => {
    const changes = []

    if (!oldState || !newState) return changes

    // Handle the case where state is a plain string (end block: 'end' or 'repeat')
    if (typeof newState === 'string' || typeof oldState === 'string') {
        if (oldState !== newState) {
            changes.push({
                action: 'SET_STATE',
                hardwareKey: 'state',
                expectedValue: newState,
                uiTarget: 'btn_toggle_repeat',
                message:
                    newState === 'repeat'
                        ? 'Click the Repeat icon to loop the program.'
                        : `Set the end state to "${newState}".`,
            })
        }
        return changes
    }

    // Both are objects — diff keys
    const allKeys = new Set([
        ...Object.keys(oldState || {}),
        ...Object.keys(newState || {}),
    ])

    for (const key of allKeys) {
        const oldVal = oldState[key]
        const newVal = newState[key]

        if (oldVal === newVal) continue

        // Skip live button keys — they're transient UI state
        if (key.includes('liveBtn') || key.includes('isLive')) continue

        const isBooleanToggle =
            typeof newVal === 'boolean' ||
            (typeof oldVal === 'boolean' && typeof newVal === 'boolean')

        // Determine action type
        let action = 'SET_VALUE'
        if (isBooleanToggle && key.startsWith('assign')) {
            action = 'TOGGLE_HARDWARE'
        } else if (key === 'state') {
            action = 'SET_STATE'
        }

        // Build a human-readable uiTarget
        let uiTarget = `input_${key}`
        if (action === 'TOGGLE_HARDWARE') {
            uiTarget = `port_${key.replace('assign', '')}`
        }

        // Build message
        let message = ''
        const label = BLOCK_LABELS[blockType] || blockType || 'block'
        if (action === 'TOGGLE_HARDWARE') {
            const portName = key.replace('assign', '')
            message = newVal
                ? `Enable "${PORT_NAME_MAP[portName]}".`
                : `Disable "${PORT_NAME_MAP[portName]}".`
        } else if (action === 'SET_VALUE') {
            const portName = key.replace('value', '')
            // message = `Set ${portName} to ${newVal}.`
            message = `Set value to ${newVal}.`
        } else if (action === 'SET_STATE') {
            message = `Set state to "${newVal}".`
        }

        changes.push({
            action,
            hardwareKey: key,
            expectedValue: newVal,
            uiTarget,
            message,
        })
    }

    return changes
}

// ──────────────────────────────────────────────
// Motor companion-port filtering
// ──────────────────────────────────────────────

/**
 * Two ports form a dual-motor pair when they share the same letter prefix
 * and have consecutive numbers (e.g. M1/M2, M3/M4).
 */
const areMotorPortPair = (portA, portB) => {
    const mA = portA.match(/^([A-Za-z]+)(\d+)$/)
    const mB = portB.match(/^([A-Za-z]+)(\d+)$/)
    if (!mA || !mB || mA[1] !== mB[1]) return false
    return Math.abs(parseInt(mA[2]) - parseInt(mB[2])) === 1
}

/**
 * Filter out redundant companion-port diffs produced by dual-port motor controls.
 *
 * DC / geared / dynamex motors use two ports (e.g. M1 for reverse, M2 for forward).
 * The slider and checkbox fire onChange for BOTH ports, but only the user-facing
 * change should appear as a tutorial substep.
 *
 * Rules applied:
 * - SET_VALUE to 0 is dropped when the paired port has a non-zero SET_VALUE.
 * - When both SET_VALUEs are 0, only the lower-numbered port is kept.
 * - TOGGLE_HARDWARE is dropped for the companion port when the primary port
 *   has the same toggle earlier in the batch.
 */
export const filterMotorCompanionDiffs = (diffs) => {
    return diffs.filter((diff, idx) => {
        const key = diff.hardwareKey

        if (diff.action === 'SET_VALUE' && key.startsWith('value')) {
            const port = key.slice(5)
            for (let oi = 0; oi < diffs.length; oi++) {
                if (oi === idx) continue
                const other = diffs[oi]
                if (
                    other.action !== 'SET_VALUE' ||
                    !other.hardwareKey.startsWith('value')
                )
                    continue
                const otherPort = other.hardwareKey.slice(5)
                if (!areMotorPortPair(port, otherPort)) continue
                // Paired motor port found
                if (diff.expectedValue === 0 && other.expectedValue !== 0)
                    return false
                if (diff.expectedValue === 0 && other.expectedValue === 0) {
                    const myNum = parseInt(port.match(/\d+$/)?.[0] || '0')
                    const otherNum = parseInt(
                        otherPort.match(/\d+$/)?.[0] || '0'
                    )
                    if (myNum > otherNum) return false
                }
                break
            }
        }

        if (diff.action === 'TOGGLE_HARDWARE' && key.startsWith('assign')) {
            const port = key.slice(6)
            const hasPriorPaired = diffs.some(
                (other, oi) =>
                    oi < idx &&
                    other.action === 'TOGGLE_HARDWARE' &&
                    other.hardwareKey.startsWith('assign') &&
                    other.expectedValue === diff.expectedValue &&
                    areMotorPortPair(port, other.hardwareKey.slice(6))
            )
            if (hasPriorPaired) return false
        }

        return true
    })
}

// ──────────────────────────────────────────────
// Build final v2.0 JSON payload
// ──────────────────────────────────────────────

/**
 * Assemble the complete v2.0 tutorial JSON.
 */
export const buildTutorialPayload = (codeSteps, meta = {}) => {
    return {
        tutorialId: meta.tutorialId || 'untitled',
        version: '2.0',
        meta: {
            title: meta.title || 'Untitled Tutorial',
            description: meta.description || '',
        },
        codeSteps: codeSteps.map((step, index) => ({
            ...step,
            stepId: index + 1, // re-number sequentially
            subSteps: (step.subSteps || []).map((sub, si) => ({
                ...sub,
                subStepId: `${index + 1}-${si + 1}`,
            })),
        })),
    }
}

export default {
    computeAllBlockPositions,
    getGridPositionForLastBlock,
    generateBoardTarget,
    generateUiTarget,
    createCodeStep,
    createInsertStep,
    createDeleteStep,
    createSubStep,
    diffPanelState,
    filterMotorCompanionDiffs,
    buildTutorialPayload,
}
